(function () {
  const equalizerByList = new WeakMap();
  const mobileMediaQuery = window.matchMedia('(max-width: 699px)');
  let mobileScrollTimer = null;
  let isMobileScrolling = false;

  function isMobileViewport() {
    return mobileMediaQuery.matches;
  }

  function markMobileScrollActivity() {
    if (!isMobileViewport()) {
      return;
    }

    isMobileScrolling = true;
    clearTimeout(mobileScrollTimer);
    mobileScrollTimer = setTimeout(() => {
      isMobileScrolling = false;
      scheduleInitialize(document);
    }, 150);
  }

  function shouldDeferEqualize() {
    return isMobileViewport() && isMobileScrolling;
  }

  function getVisibleInfoBlocks(productList) {
    return Array.from(productList.querySelectorAll('.pct-card__info')).filter((info) => {
      const wrapper = info.closest('[data-product-wrapper]');
      if (!wrapper) {
        return true;
      }
      return wrapper.style.display !== 'none' && !wrapper.hidden;
    });
  }

  function measureNaturalHeight(info) {
    const previousMinHeight = info.style.minHeight;
    info.style.minHeight = '';
    const height = Math.ceil(info.getBoundingClientRect().height);
    info.style.minHeight = previousMinHeight;
    return height;
  }

  function equalizeInfoHeights(productList) {
    const infos = getVisibleInfoBlocks(productList);
    if (infos.length === 0) {
      return;
    }

    const maxHeight = Math.max(0, ...infos.map(measureNaturalHeight));
    if (maxHeight === 0) {
      return;
    }

    const minHeight = `${maxHeight}px`;
    const needsUpdate = infos.some((info) => info.style.minHeight !== minHeight);
    if (!needsUpdate) {
      return;
    }

    infos.forEach((info) => {
      info.style.minHeight = minHeight;
    });
  }

  class PctCardInfoEqualizer {
    constructor(productList) {
      this.productList = productList;
      this.isUpdating = false;
      this.rafId = null;
      this.infoObserver = null;

      this.onResize = () => {
        if (shouldDeferEqualize()) {
          return;
        }
        this.schedule();
      };
      this.onListMutation = () => {
        if (shouldDeferEqualize()) {
          return;
        }
        this.schedule();
      };

      this.listResizeObserver = new ResizeObserver(this.onResize);
      this.listResizeObserver.observe(productList);

      this.listMutationObserver = new MutationObserver(this.onListMutation);
      this.listMutationObserver.observe(productList, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'hidden', 'class'],
      });

      this.bindInfoObservers();
      this.schedule();
    }

    bindInfoObservers() {
      if (this.infoObserver) {
        this.infoObserver.disconnect();
      }

      this.infoObserver = new ResizeObserver(this.onResize);
      getVisibleInfoBlocks(this.productList).forEach((info) => {
        this.infoObserver.observe(info);
      });
    }

    schedule() {
      if (shouldDeferEqualize()) {
        return;
      }

      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
      }

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.run();
      });
    }

    run() {
      if (shouldDeferEqualize() || this.isUpdating) {
        return;
      }

      this.isUpdating = true;

      if (this.infoObserver) {
        this.infoObserver.disconnect();
      }

      equalizeInfoHeights(this.productList);
      this.bindInfoObservers();
      this.isUpdating = false;
    }

    destroy() {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
      }

      this.listResizeObserver.disconnect();
      this.listMutationObserver.disconnect();

      if (this.infoObserver) {
        this.infoObserver.disconnect();
      }

      getVisibleInfoBlocks(this.productList).forEach((info) => {
        info.style.minHeight = '';
      });

      equalizerByList.delete(this.productList);
    }
  }

  function findProductLists(root) {
    if (!root.querySelectorAll) {
      return [];
    }

    return Array.from(root.querySelectorAll('.product-list')).filter((list) =>
      list.querySelector('.pct-card__info')
    );
  }

  function initializePctCardInfoHeights(root = document) {
    findProductLists(root).forEach((productList) => {
      if (equalizerByList.has(productList)) {
        equalizerByList.get(productList).schedule();
        return;
      }

      equalizerByList.set(productList, new PctCardInfoEqualizer(productList));
    });
  }

  function destroyPctCardInfoHeights(root) {
    findProductLists(root).forEach((productList) => {
      const equalizer = equalizerByList.get(productList);
      if (equalizer) {
        equalizer.destroy();
      }
    });
  }

  function scheduleInitialize(root = document) {
    requestAnimationFrame(() => {
      initializePctCardInfoHeights(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializePctCardInfoHeights(document));
  } else {
    initializePctCardInfoHeights(document);
  }

  window.addEventListener('resize', () => scheduleInitialize(document), { passive: true });
  window.addEventListener('scroll', markMobileScrollActivity, { passive: true, capture: true });
  window.addEventListener('touchmove', markMobileScrollActivity, { passive: true, capture: true });

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      scheduleInitialize(event.target || document);
    },
    { passive: true }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyPctCardInfoHeights(event.target || document);
    },
    { passive: true }
  );

  document.addEventListener(
    'facets:updated',
    () => {
      scheduleInitialize(document);
    },
    { passive: true }
  );

  document.documentElement.addEventListener(
    'theme:loading:end',
    () => {
      scheduleInitialize(document);
    },
    { passive: true }
  );

  window.initializePctCardInfoHeights = initializePctCardInfoHeights;
})();
